"use client";

import { postContact } from "@/apis/queryFunctions/contact";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ActionButton from "@/components/Buttons/ActionButton";
import { z } from "zod";
import { usePathname } from "next/navigation";
import { dictionaries } from "@/utils/i18n";
import { useToast } from "@/context/ToastProvider";

const contactSchema = z.object({
  ho_ten: z.string().min(1, "Vui lòng nhập họ tên của quý khách"),
  dien_thoai: z
    .string()
    .min(1, "Vui lòng nhập số điện thoại")
    .regex(/^(0|\+84)[0-9]{9}$/, "Số điện thoại không hợp lệ"),
  email: z.string().email("Email không hợp lệ").optional().or(z.literal("")),
  dia_chi: z.string().optional(),
  noi_dung: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function Contact() {
  const pathname = usePathname();
  const locale = pathname.split("/")[1] || "vi";
  const dictionary = dictionaries[locale as "vi" | "en"];

  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      if (rs && Object.keys(rs).length > 0) {
        showToast(
          dictionary?.contact.toast?.success || "Gửi thông tin thành công",
          "success"
        );
        reset();
      } else {
        showToast(
          dictionary?.contact.toast?.error || "Gửi thông tin không thành công",
          "error"
        );
      }
    },
    onError: () => {
      showToast(
        dictionary?.contact.toast?.error || "Có lỗi xảy ra, vui lòng thử lại",
        "error"
      );
    },
  });

  const onSubmit = (data: ContactFormData) => {
    contactMutation.mutate(data);
  };

  const inputClass =
    "w-full bg-white/95 border border-white/30 px-4 py-3 text-sm text-primary placeholder:text-primary/50 outline-none transition focus:border-white focus:ring-2 focus:ring-white/30";

  return (
    <div className="relative z-10 w-full max-w-md">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        {/* HỌ TÊN */}
        <div>
          <input
            type="text"
            placeholder={`${dictionary?.contact.placeholder.fullName} *`}
            {...register("ho_ten")}
            className={inputClass}
          />
          {errors.ho_ten && (
            <p className="mt-1 text-xs text-yellow-300">
              {errors.ho_ten.message}
            </p>
          )}
        </div>

        {/* ĐIỆN THOẠI */}
        <div>
          <input
            type="text"
            placeholder={`${dictionary?.contact.placeholder.phone} *`}
            {...register("dien_thoai")}
            className={inputClass}
          />
          {errors.dien_thoai && (
            <p className="mt-1 text-xs text-yellow-300">
              {errors.dien_thoai.message}
            </p>
          )}
        </div>

        {/* EMAIL */}
        <div>
          <input
            type="email"
            placeholder={dictionary?.contact.placeholder.email}
            {...register("email")}
            className={inputClass}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-yellow-300">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* NỘI DUNG */}
        <textarea
          rows={3}
          placeholder={dictionary?.contact.placeholder.content}
          {...register("noi_dung")}
          className={`${inputClass} resize-none`}
        />

        {/* BUTTON */}
        <div className="pt-4">
          <ActionButton
            type="submit"
            label={
              contactMutation.isPending
                ? dictionary?.contact.form.loading || "Đang gửi..."
                : dictionary?.contact.form.button
            }
            className={
              contactMutation.isPending
                ? "opacity-60 pointer-events-none"
                : "font-semibold uppercase tracking-wide bg-[#6B5542] text-white transition shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
            }
          />
        </div>
      </form>
    </div>
  );
}
